import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = logged out, object = logged in
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Quick optimization: don't call /auth/me at all if there's no auth cookie
    // present. This avoids a noisy 401 in the browser console on every public
    // page visit by anonymous users. The access_token cookie is set httpOnly so
    // document.cookie can't read it directly — we keep a non-httpOnly marker
    // cookie `has_session=1` set/cleared on login/logout instead.
    if (typeof document !== "undefined" && !document.cookie.includes("has_session=")) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, referralCode) => {
    const body = { email, password, name };
    if (referralCode && referralCode.trim()) body.referral_code = referralCode.trim().toUpperCase();
    const { data } = await api.post("/auth/register", body);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
